import type { ReactNode } from 'react';

export interface MarqueeLabelProps {
  /** Text-like content to clamp (string, number, or an array of those). */
  label: ReactNode;
  /**
   * Optional fixed clamp width in px — supplied by hosts with a fixed budget
   * (`Breadcrumb.maxLabelWidth`, `Tag.maxLabelWidth`). Omit for FLUID mode:
   * the label fills its parent (renders `block w-full`), so the PARENT must
   * constrain the width — a `min-w-0` flex child (Badge chip labels, DeckCard
   * titles, MenuListItem labels).
   */
  maxWidth?: number;
  /** Typography classes from the host component (e.g. `breadcrumbTextVariants()`, `tagTextVariants()`). */
  className?: string;
  /**
   * How the clamp ENDS when the label is too long for its box.
   *
   *   - `ellipsis` (default) — the classic `…`.
   *   - `fade` — the text runs to the box edge and dissolves under a
   *     {@link MARQUEE_FADE_PX}-wide alpha ramp, the same treatment the app
   *     uses at every scroll edge (`ScrollFade`, size `xs`). Reads as "there
   *     is more here" without spending a character on the `…`, so a clamped
   *     label shows MORE of its text at the same width — which is why the DS
   *     cards use it for their titles.
   *
   * The ramp is a MASK, not a colour overlay: it fades to transparent, so it
   * works over the cards' translucent glass panel, in dark mode, and over
   * whatever cover art shows through — none of which a gradient painted in
   * the surface colour could manage.
   *
   * CALLER CONTRACT — `fade` needs a box WIDER than its text to stay honest.
   * The ramp mounts only once the laid-out line reaches the box edge, so in
   * fluid mode the parent has to hand the label the leftover width, not the
   * text's own: a column child gets that for free (RN stretches it, web's
   * `w-full` fills it), but a child of a flex ROW must be given `flex-1`
   * (`min-w-0 flex-1` in {@link MarqueeLabelProps.className}). Leave it to
   * shrink-to-fit and the box hugs the line, the two widths match by
   * construction, and EVERY label fades — a screen title reading "Library"
   * dissolves its own trailing `y`. The class is safe to pass: it is merged
   * away on the inner Text, which always ends up `flex-none`.
   */
  overflow?: 'ellipsis' | 'fade';
  /**
   * One-shot "announce" pass. While true the label is its host's CURRENT item
   * (a sidebar row whose route was just opened): on becoming true — or
   * mounting true — the label waits {@link PLAY_ONCE_DELAY_MS}, then, if the
   * text is actually truncated, plays a SINGLE there-and-back marquee pass
   * and comes to rest. It never loops: an unbounded marquee on a row the user
   * is no longer interacting with is exactly the eye-pull this prop replaces.
   * Flipping back to false cancels a pending or mid-flight pass. The
   * pointer-driven marquees (hover on web/iPad pointers, press-and-hold on
   * touch) are independent of this and still run/stop with their pointer.
   */
  playOnce?: boolean;
  /**
   * E2E handle for the TEXT itself: `testID` on the native Text,
   * `data-testid` on the web span. Lets a flow read a screen title by id
   * instead of matching its words.
   */
  testID?: string;
}

/**
 * Width of the `fade` ramp. `ScrollFade`'s `xs` band (24px) — the app's
 * thinnest fade — so a clamped label dissolves at the same rate as every
 * other fading edge in the product.
 */
export const MARQUEE_FADE_PX = 24;

/**
 * Dwell between a label becoming its host's current item and the
 * {@link MarqueeLabelProps.playOnce} pass starting — long enough for the
 * row's activation transition (highlight fill, auto-reveal scroll) to settle
 * so the pass reads as a follow-up, not part of the navigation motion.
 */
export const PLAY_ONCE_DELAY_MS = 200;
